import { gsap } from 'gsap';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavigateOptions, To, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@column/column-ui-kit';

import { Logo, Panel, animateLogo } from '../_components';
import { ROUTE as APP_ROUTE, ROUTE_PREFIX as APP_ROUTE_PREFIX } from '~/app/routes';
import { useQueryParams } from '~/hooks';
import { useNavigate } from '~/lib/navigation';
import { ROUTE as PUBLIC, ROUTE } from '~/public/routes';
import { ChallengeMFAResponse, ChallengeName, DashboardLoginResponse, MFAStatus, PlaidParams } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Paragraph } from '~/styles';

import { MultifactorAuthForm, PasswordAuthForm } from './_partials';

interface PageLoginProps {
  isAuthFlow?: boolean;
}

export type LoginQueryParams = {
  [key: string]: string;
  redirect: string;
};

enum LoginView {
  PASSWORD = 'password',
  MFA = 'mfa',
}

interface PassswordState {
  view: LoginView.PASSWORD;
}

interface MultifactorState {
  view: LoginView.MFA;
  challengeName: ChallengeName;
  phoneNumber?: string;
  sessionId: string;
}

type LoginState = PassswordState | MultifactorState;

const AdditionalLogo = styled(Logo)`
  margin-left: -12px;
  transform-origin: 0 0;
  overflow: visible;

  & > path {
    --logo-path-opacity: 0;
    --logo-path-x: 16px;
    --logo-path-y: 24px;
    opacity: var(--logo-path-opacity);
    transform: translate(var(--logo-path-x), var(--logo-path-y)) translateZ(0);
  }
`;

const HiddenLogo = styled(Logo)`
  opacity: 0;
`;

const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
`;

const normalizeRedirectUrl = (url: string | null): string | undefined => {
  if (!url) {
    return undefined;
  }

  const isAbsoluteUrl = (testUrl: string): boolean => /^(https?:\/\/)/i.test(testUrl);
  const getRelativeUrl = (testUrl: string): string => (testUrl.startsWith('/') ? testUrl : `/${testUrl}`);

  if (isAbsoluteUrl(url)) {
    return url;
  }

  return getRelativeUrl(url);
};

export const PageLogin: React.FC<PageLoginProps> = ({ isAuthFlow }) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const { setSession, signIn, addParam, params } = useSessionStore();
  const { getQueryParam, hasQueryParam } = useQueryParams<LoginQueryParams>();
  const logoWrapperRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectUrl = normalizeRedirectUrl(getQueryParam('redirect'));

  const [loginState, setLoginState] = useState<LoginState>({
    view: LoginView.PASSWORD,
  });

  const navigateWithAnimation = useCallback((to: To, options?: NavigateOptions) => {
    const logo = logoRef.current;
    const logoWrapper = logoWrapperRef.current;
    const panel = panelRef.current;

    if (!logo || !logoWrapper || !panel) {
      navigate(to, options);
      return;
    }

    // Lock the panel dimensions to prevent layout shifts during animation
    const panelRect = panel.getBoundingClientRect();
    panel.style.width = `${panelRect.width}px`;
    panel.style.height = `${panelRect.height}px`;

    animateLogo(logo, logoWrapper, panel, gsap, MotionPathPlugin, () => navigate(to, options));
  }, []);

  const onLogin = useCallback(
    (sessionId: string, dashboardUserId: string, mfaStatus: MFAStatus) => {
      setSession(sessionId, dashboardUserId, { mfaStatus });

      signIn()
        .then(() => {
          if (isAuthFlow && params) {
            navigate(PUBLIC.PLAID_SELECT);
            return;
          }

          const destinationUrl = redirectUrl || APP_ROUTE.ROOT;

          if (destinationUrl.startsWith(APP_ROUTE_PREFIX)) {
            navigateWithAnimation(destinationUrl, {
              state: { from: 'auth' },
            });
            return;
          }
          navigate(destinationUrl, {
            state: { from: 'auth' },
          });
        })
        .catch((error) => {
          addDangerNotification({
            content: error.message,
            display: 'page',
          });
        });
    },
    [navigateWithAnimation, isAuthFlow, params, redirectUrl]
  );

  const onPasswordSuccess = useCallback(
    (response: DashboardLoginResponse) => {
      if (!response.challengeName || response.challengeName === ChallengeName.CHALLENGE_NONE) {
        onLogin(response.sessionId, response.dashboardUserId, response.mfaStatus);
        return;
      }

      setLoginState({
        view: LoginView.MFA,
        challengeName: response.challengeName,
        phoneNumber: response.phoneNumber,
        sessionId: response.sessionId,
      });
    },
    [onLogin]
  );

  const onMultifactorSuccess = useCallback(
    (sessionId: string, response: ChallengeMFAResponse) => {
      onLogin(sessionId, response.dashboardUserId, response.mfaStatus);
    },
    [onLogin]
  );

  useEffect(() => {
    Object.keys(PlaidParams).forEach((key: string) => {
      if (hasQueryParam(key)) {
        addParam(key, getQueryParam(key) as string | undefined);
      }
    });
  }, [location.search]);

  return (
    <>
      {!isAuthFlow && (
        <LogoWrapper ref={logoWrapperRef}>
          <AdditionalLogo size="24px" variant="full" ref={logoRef} />
        </LogoWrapper>
      )}

      <Panel ref={panelRef}>
        {!isAuthFlow && <HiddenLogo size="24px" />}
        {loginState.view === LoginView.PASSWORD && (
          <PasswordAuthForm showForgotPassword={!isAuthFlow} redirectUrl={redirectUrl} onSuccess={onPasswordSuccess} />
        )}
        {loginState.view === LoginView.MFA && (
          <MultifactorAuthForm
            challengeName={loginState.challengeName}
            phoneNumber={loginState.phoneNumber}
            onCancel={() => setLoginState({ view: LoginView.PASSWORD })}
            onSuccess={(response) => onMultifactorSuccess(loginState.sessionId, response)}
          />
        )}
        {loginState.view === LoginView.PASSWORD && !isAuthFlow && (
          <Flex flexDirection="column" gap={16}>
            <Paragraph size="small">Don't have a Column account?</Paragraph>
            <Button variant="secondary" onClick={() => navigate(ROUTE.REGISTER)}>
              Create New Account
            </Button>
          </Flex>
        )}
      </Panel>
    </>
  );
};
